// src/shared/components/ChunkErrorBoundary.tsx
// ────────────────────────────────────────────────────────────────────────────
// Last-resort error boundary for chunk-load failures that survive the
// lazyWithRetry auto-reload.  Shows a minimal full-screen "reload" prompt
// using pac-glass design tokens.
// ────────────────────────────────────────────────────────────────────────────
import React, { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props { children: ReactNode; }
interface State { hasError: boolean; }

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State | null {
    const isChunk =
      error.name === 'ChunkLoadError' ||
      /loading chunk .* failed/i.test(error.message ?? '');
    return isChunk ? { hasError: true } : null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ChunkErrorBoundary]', error, info);
  }

  handleReload = () => {
    sessionStorage.removeItem('pac_chunk_reload');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen grid place-items-center bg-black text-neutral-300">
          <div className="pac-glass-raised rounded-2xl p-8 max-w-sm text-center space-y-4">
            <p className="text-lg font-semibold text-white">
              New version available
            </p>
            <p className="text-sm text-neutral-400">
              A deployment happened while the app was open.
              Reload to get the latest version.
            </p>
            <button
              onClick={this.handleReload}
              className="pac-glass-btn px-6 py-2 rounded-lg border border-emerald-500/40
                         text-emerald-400 hover:border-emerald-400 transition"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
